/**
 * Cena hero — composição editorial com elementos:
 * - Capelo de formatura tridimensional
 * - Livro aberto com folhas
 * - Sparkles e estrelas flutuantes
 * - Blob de cor de fundo
 * Tudo desenhado em SVG inline (sem dependência externa).
 */
export function HeroScene({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 400 400"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <defs>
        <linearGradient id="grad-violet" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(255 80% 65%)" />
          <stop offset="100%" stopColor="hsl(280 70% 55%)" />
        </linearGradient>
        <linearGradient id="grad-pink" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(340 90% 75%)" />
          <stop offset="100%" stopColor="hsl(15 90% 70%)" />
        </linearGradient>
        <linearGradient id="grad-mint" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(160 65% 65%)" />
          <stop offset="100%" stopColor="hsl(180 70% 60%)" />
        </linearGradient>
        <linearGradient id="grad-book" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="hsl(0 0% 100%)" />
          <stop offset="100%" stopColor="hsl(255 30% 92%)" />
        </linearGradient>
      </defs>

      {/* Blobs de fundo */}
      <g opacity="0.6">
        <circle cx="60" cy="80" r="60" fill="url(#grad-pink)" opacity="0.45" />
        <circle cx="340" cy="320" r="70" fill="url(#grad-mint)" opacity="0.4" />
        <circle cx="320" cy="60" r="45" fill="url(#grad-violet)" opacity="0.35" />
      </g>

      {/* Pilha de livros base (decorativa) */}
      <g transform="translate(120, 280)">
        <rect width="160" height="22" rx="5" fill="hsl(45 95% 70%)" />
        <rect y="-26" x="10" width="140" height="22" rx="5" fill="hsl(15 90% 70%)" />
        <rect y="-52" x="20" width="120" height="22" rx="5" fill="hsl(340 85% 72%)" />
      </g>

      {/* Capelo de formatura */}
      <g transform="translate(200, 180)">
        {/* base do capelo */}
        <ellipse cx="0" cy="20" rx="60" ry="14" fill="hsl(255 30% 15%)" />
        {/* topo quadrado */}
        <polygon
          points="-80,0 80,0 60,-18 -60,-18"
          fill="url(#grad-violet)"
        />
        <polygon
          points="-80,0 80,0 60,-18 -60,-18"
          fill="hsl(255 30% 15%)"
          opacity="0.0"
        />
        {/* borda escura */}
        <polygon
          points="-80,0 80,0 60,-18 -60,-18"
          stroke="hsl(255 50% 35%)"
          strokeWidth="2"
          fill="none"
        />
        {/* tassel (cordão) */}
        <line x1="40" y1="-9" x2="55" y2="40" stroke="hsl(45 95% 60%)" strokeWidth="3" strokeLinecap="round" />
        <circle cx="55" cy="45" r="7" fill="hsl(45 95% 60%)" />
      </g>

      {/* Livro aberto flutuando */}
      <g transform="translate(105, 175) rotate(-12)" className="animate-float">
        <path
          d="M 0 0 Q 30 -10 60 0 L 60 50 Q 30 40 0 50 Z"
          fill="url(#grad-book)"
          stroke="hsl(255 20% 80%)"
          strokeWidth="1.5"
        />
        <path
          d="M 60 0 Q 90 -10 120 0 L 120 50 Q 90 40 60 50 Z"
          fill="url(#grad-book)"
          stroke="hsl(255 20% 80%)"
          strokeWidth="1.5"
        />
        {/* linhas de texto */}
        <line x1="10" y1="12" x2="50" y2="9" stroke="hsl(255 20% 70%)" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="10" y1="22" x2="48" y2="20" stroke="hsl(255 20% 70%)" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="10" y1="32" x2="46" y2="30" stroke="hsl(255 20% 70%)" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="68" y1="12" x2="110" y2="10" stroke="hsl(255 20% 70%)" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="68" y1="22" x2="108" y2="20" stroke="hsl(255 20% 70%)" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="68" y1="32" x2="106" y2="30" stroke="hsl(255 20% 70%)" strokeWidth="1.2" strokeLinecap="round" />
      </g>

      {/* Estrelas e sparkles */}
      <g className="animate-float" style={{ animationDelay: "0.3s" }}>
        <Star x={280} y={120} size={14} fill="hsl(45 95% 65%)" />
      </g>
      <g className="animate-float" style={{ animationDelay: "1s" }}>
        <Star x={330} y={200} size={10} fill="hsl(340 85% 72%)" />
      </g>
      <g className="animate-float" style={{ animationDelay: "1.5s" }}>
        <Star x={70} y={300} size={12} fill="hsl(160 60% 60%)" />
      </g>
      <g className="animate-float" style={{ animationDelay: "0.7s" }}>
        <Sparkle x={310} y={300} size={20} fill="hsl(255 78% 62%)" />
      </g>
      <g className="animate-float" style={{ animationDelay: "1.8s" }}>
        <Sparkle x={50} y={130} size={16} fill="hsl(255 78% 62%)" />
      </g>
    </svg>
  );
}

function Star({ x, y, size, fill }: { x: number; y: number; size: number; fill: string }) {
  // 5-pointed star em path
  const points: Array<[number, number]> = [];
  for (let i = 0; i < 10; i++) {
    const angle = (Math.PI * 2 * i) / 10 - Math.PI / 2;
    const r = i % 2 === 0 ? size : size * 0.45;
    points.push([Math.cos(angle) * r, Math.sin(angle) * r]);
  }
  const d = points.map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`)).join(" ") + " Z";
  return (
    <g transform={`translate(${x}, ${y})`}>
      <path d={d} fill={fill} />
    </g>
  );
}

function Sparkle({ x, y, size, fill }: { x: number; y: number; size: number; fill: string }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <path
        d={`M 0 -${size} Q ${size * 0.3} -${size * 0.3} ${size} 0 Q ${size * 0.3} ${size * 0.3} 0 ${size} Q -${size * 0.3} ${size * 0.3} -${size} 0 Q -${size * 0.3} -${size * 0.3} 0 -${size} Z`}
        fill={fill}
      />
    </g>
  );
}
